import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

// ── Google SVG ────────────────────────────────────────────────────────────────
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908C17.658 14.07 17.64 11.877 17.64 9.2z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
      <path d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}

// ── Feature rows ──────────────────────────────────────────────────────────────
const FEATURES = [
  {
    icon: '◎',
    title: 'cross-domain intelligence',
    body: 'your race is in 11 days, you haven\'t logged a run this week, and you have 3 evening shifts — mylo connects the dots you\'d otherwise miss.',
  },
  {
    icon: '◈',
    title: 'one coach, every domain',
    body: 'tasks, training, finances, vinyl, calendar — one AI that knows your full context and remembers what matters.',
  },
  {
    icon: '◇',
    title: 'built for your actual life',
    body: 'morning brief, streak tracking, Discogs sync, Google Calendar, offline-first — tools that fit the way you actually live.',
  },
];

// ── Animated word cycling ─────────────────────────────────────────────────────
const CYCLING = ['training', 'finances', 'schedule', 'vinyl', 'goals', 'habits'];

export default function LoginScreen() {
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [wordIdx,   setWordIdx]   = useState(0);
  const [visible,   setVisible]   = useState(true);
  const [mounted,   setMounted]   = useState(false);

  // Entrance animation
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  // Word cycling
  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setWordIdx(i => (i + 1) % CYCLING.length);
        setVisible(true);
      }, 280);
    }, 2200);
    return () => clearInterval(interval);
  }, []);

  async function handleGoogleLogin() {
    if (!supabase) { setError('auth not configured — check environment variables'); return; }
    setLoading(true);
    setError('');
    try {
      const { error: authError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      });
      if (authError) { setError(authError.message); setLoading(false); }
    } catch (e) {
      setError(e.message || 'login failed');
      setLoading(false);
    }
  }

  return (
    <div data-theme="dark" style={{
      position: 'fixed', inset: 0,
      background: 'var(--bg)',
      overflowY: 'auto',
      fontFamily: 'var(--sans)',
    }}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes wordIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes wordOut {
          from { opacity: 1; transform: translateY(0); }
          to   { opacity: 0; transform: translateY(-6px); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50%       { opacity: 0.8; }
        }
        .landing-fade-1 { animation: fadeUp 0.5s cubic-bezier(0.4,0,0.2,1) 0.05s both; }
        .landing-fade-2 { animation: fadeUp 0.5s cubic-bezier(0.4,0,0.2,1) 0.15s both; }
        .landing-fade-3 { animation: fadeUp 0.5s cubic-bezier(0.4,0,0.2,1) 0.25s both; }
        .landing-fade-4 { animation: fadeUp 0.5s cubic-bezier(0.4,0,0.2,1) 0.35s both; }
        .landing-fade-5 { animation: fadeUp 0.5s cubic-bezier(0.4,0,0.2,1) 0.45s both; }
        .word-in  { animation: wordIn  0.28s cubic-bezier(0.4,0,0.2,1) both; }
        .word-out { animation: wordOut 0.28s cubic-bezier(0.4,0,0.2,1) both; }
        .signin-btn:hover { border-color: var(--b3) !important; background: var(--s2) !important; }
        .signin-btn:active { transform: scale(0.98); }
        .feature-row:not(:last-child) { border-bottom: 0.5px solid var(--b1); }
      `}</style>

      <div style={{
        maxWidth: 480,
        margin: '0 auto',
        padding: '0 24px 80px',
        opacity: mounted ? 1 : 0,
        transition: 'opacity 0.2s ease',
      }}>

        {/* ── Wordmark ── */}
        <div className="landing-fade-1" style={{
          paddingTop: 64,
          marginBottom: 52,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            {/* M mark */}
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'var(--accent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="16" height="13" viewBox="0 0 16 13" fill="none">
                <path d="M1 12V1l7 9 7-9v11" stroke="var(--on-accent)" strokeWidth="1.6"
                  strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span style={{
              fontSize: 22, fontWeight: 300, color: 'var(--ink)',
              letterSpacing: '-0.4px', lineHeight: 1,
            }}>mylo</span>
          </div>
        </div>

        {/* ── Hero ── */}
        <div className="landing-fade-2" style={{ marginBottom: 48 }}>
          <h1 style={{
            fontSize: 'clamp(32px, 7vw, 44px)',
            fontWeight: 300,
            color: 'var(--ink)',
            letterSpacing: '-0.8px',
            lineHeight: 1.15,
            margin: '0 0 16px',
          }}>
            your chief of staff<br />
            for{' '}
            <span
              className={visible ? 'word-in' : 'word-out'}
              style={{
                display: 'inline-block',
                color: 'var(--accent)',
                minWidth: 100,
              }}
            >
              {CYCLING[wordIdx]}
            </span>
          </h1>
          <p style={{
            fontSize: 16, fontWeight: 300, color: 'var(--t2)',
            lineHeight: 1.65, margin: 0, maxWidth: 400,
          }}>
            mylo connects your training, finances, calendar, and tasks —
            then tells you what actually matters today.
          </p>
        </div>

        {/* ── Sign in ── */}
        <div className="landing-fade-3" style={{ marginBottom: 48 }}>
          <button
            className="signin-btn"
            onClick={handleGoogleLogin}
            disabled={loading}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              width: '100%', padding: '14px 20px',
              background: 'var(--s1)',
              border: '0.5px solid var(--b2)',
              borderRadius: 12,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--sans)', fontSize: 15, fontWeight: 400,
              color: loading ? 'var(--t3)' : 'var(--ink)',
              transition: 'border-color 0.12s, background 0.12s, transform 0.08s',
              WebkitTapHighlightColor: 'transparent',
              letterSpacing: '-0.1px',
            }}
          >
            {loading ? (
              <>
                {[0,1,2].map(i => (
                  <span key={i} style={{
                    width: 5, height: 5, borderRadius: '50%',
                    background: 'var(--t3)', display: 'inline-block',
                    animation: `pulse 1s ease-in-out ${i * 0.18}s infinite`,
                  }} />
                ))}
              </>
            ) : (
              <>
                <GoogleIcon />
                continue with google
              </>
            )}
          </button>

          {error && (
            <div style={{
              marginTop: 10, padding: '9px 12px',
              background: 'var(--redbg)', border: '0.5px solid var(--redbr)',
              borderRadius: 8, fontSize: 12, color: 'var(--red)', lineHeight: 1.5,
            }}>
              {error}
            </div>
          )}

          <div style={{
            marginTop: 12, fontSize: 11, color: 'var(--t4)',
            textAlign: 'center', lineHeight: 1.6,
          }}>
            your data is stored securely and never shared.
          </div>
        </div>

        {/* ── Divider ── */}
        <div className="landing-fade-4" style={{
          height: '0.5px', background: 'var(--b1)', marginBottom: 40,
        }} />

        {/* ── Features ── */}
        <div className="landing-fade-4" style={{
          background: 'var(--s1)',
          border: '0.5px solid var(--b1)',
          borderRadius: 16,
          overflow: 'hidden',
          marginBottom: 40,
        }}>
          {FEATURES.map((f, i) => (
            <div key={i} className="feature-row" style={{
              display: 'flex', gap: 16,
              padding: '18px 20px',
            }}>
              <div style={{
                fontSize: 16, color: 'var(--accent)',
                flexShrink: 0, marginTop: 2, lineHeight: 1,
                opacity: 0.7,
              }}>{f.icon}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, fontWeight: 500,
                  color: 'var(--ink)', marginBottom: 4,
                  letterSpacing: '-0.1px',
                }}>{f.title}</div>
                <div style={{
                  fontSize: 13, color: 'var(--t3)',
                  lineHeight: 1.6, fontWeight: 300,
                }}>{f.body}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Stat strip ── */}
        <div className="landing-fade-5" style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
          gap: 8, marginBottom: 48,
        }}>
          {[
            { n: '343', label: 'records synced' },
            { n: '∞',   label: 'domains connected' },
            { n: '1',   label: 'chief of staff' },
          ].map((s, i) => (
            <div key={i} style={{
              background: 'var(--s1)', border: '0.5px solid var(--b1)',
              borderRadius: 12, padding: '14px 12px',
              textAlign: 'center',
            }}>
              <div style={{
                fontSize: 22, fontWeight: 300, color: 'var(--ink)',
                letterSpacing: '-0.4px', lineHeight: 1, marginBottom: 4,
              }}>{s.n}</div>
              <div style={{
                fontSize: 10, color: 'var(--t4)',
                letterSpacing: '0.03em', lineHeight: 1.3,
              }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── Bottom CTA ── */}
        <div className="landing-fade-5" style={{ textAlign: 'center' }}>
          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            style={{
              background: 'var(--accent)', color: 'var(--on-accent)',
              border: 'none', borderRadius: 12,
              padding: '13px 32px', fontSize: 15, fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--sans)', letterSpacing: '-0.1px',
              transition: 'opacity 0.12s, transform 0.08s',
              WebkitTapHighlightColor: 'transparent',
              opacity: loading ? 0.6 : 1,
            }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.88'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
            onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.98)'; }}
            onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            get started →
          </button>
          <div style={{
            marginTop: 16, fontSize: 11, color: 'var(--t4)', lineHeight: 1.6,
          }}>
            free · no credit card · your data stays yours
          </div>
        </div>

      </div>
    </div>
  );
}
